import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useDismiss } from '@/app/useDismiss';
import { formatMoney } from '@/lib/money';
import type { Notification, NotificationListResponse } from './types';
import { SEVERITY_STYLE } from './types';

/**
 * Sininho da barra superior — RF-ALE-01.
 *
 * O contador é uma consulta separada e barata (`unread-count`), com refetch
 * periódico. Recarregar a lista inteira só para saber o número seria caro e,
 * pior, faria a lista mudar debaixo do usuário enquanto ele a lê.
 */
export function NotificationBell({ onOpenAll }: { onOpenAll: () => void }) {
  const [open, setOpen] = useState(false);
  const box = useDismiss<HTMLDivElement>(open, () => setOpen(false));
  const queryClient = useQueryClient();

  const count = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => api.get<{ data: { unread: number } }>('/notifications/unread-count'),
    select: (r) => r.data.unread,
    // Um minuto: alerta não é chat. Intervalo curto gastaria requisição para
    // um número que muda uma vez por dia, quando o avaliador roda.
    refetchInterval: 60_000,
  });

  const list = useQuery({
    queryKey: ['notifications', 'recent'],
    queryFn: () => api.get<NotificationListResponse>('/notifications?per_page=8'),
    enabled: open, // só busca quando abre
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['notifications'] });

  const markRead = useMutation({
    mutationFn: (id: number) => api.post(`/notifications/${id}/read`, {}),
    onSuccess: invalidate,
  });

  const dismiss = useMutation({
    mutationFn: (id: number) => api.post(`/notifications/${id}/dismiss`, {}),
    onSuccess: invalidate,
  });

  const readAll = useMutation({
    mutationFn: () => api.post('/notifications/read-all', {}),
    onSuccess: invalidate,
  });

  const unread = count.data ?? 0;
  const items = list.data?.data ?? [];

  return (
    <div className="relative" ref={box}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative rounded-lg p-2 text-slate-500 transition hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
        aria-label={unread > 0 ? `${unread} alertas não lidos` : 'Alertas'}
        title="Alertas"
      >
        <span className="text-lg leading-none">🔔</span>
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-600 px-1 text-[10px] font-semibold text-white">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full z-20 mt-1.5 w-[22rem] rounded-xl border border-slate-200 bg-white shadow-lg dark:border-slate-800 dark:bg-slate-900">
          <div className="flex items-center gap-2 border-b border-slate-200 px-3.5 py-2.5 dark:border-slate-800">
            <span className="text-sm font-medium">Alertas</span>
            {unread > 0 && (
              <button
                onClick={() => readAll.mutate()}
                disabled={readAll.isPending}
                className="ml-auto text-xs text-indigo-600 hover:underline dark:text-indigo-400"
              >
                Marcar todos como lidos
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {list.isPending ? (
              <div className="animate-pulse p-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="mb-2 h-12 rounded bg-slate-100 dark:bg-slate-800" />
                ))}
              </div>
            ) : items.length === 0 ? (
              /* O vazio explica o que ele significa. "Nenhum alerta" sozinho
                 deixa a dúvida entre "está tudo bem" e "não configurei nada". */
              <div className="px-4 py-8 text-center">
                <p className="text-sm text-slate-500 dark:text-slate-400">Nada para avisar agora.</p>
                <p className="mt-1 text-xs text-slate-400">
                  Os alertas dependem de regras cadastradas. Configure em{' '}
                  <button onClick={() => { setOpen(false); onOpenAll(); }} className="text-indigo-600 hover:underline dark:text-indigo-400">
                    Alertas
                  </button>
                  .
                </p>
              </div>
            ) : (
              items.map((n) => (
                <NotificationRow
                  key={n.id}
                  notification={n}
                  onRead={() => markRead.mutate(n.id)}
                  onDismiss={() => dismiss.mutate(n.id)}
                />
              ))
            )}
          </div>

          <button
            onClick={() => { setOpen(false); onOpenAll(); }}
            className="w-full border-t border-slate-200 px-3.5 py-2.5 text-center text-sm text-indigo-600 hover:bg-slate-50 dark:border-slate-800 dark:text-indigo-400 dark:hover:bg-slate-800"
          >
            Ver todos e configurar
          </button>
        </div>
      )}
    </div>
  );
}

function NotificationRow({
  notification,
  onRead,
  onDismiss,
}: {
  notification: Notification;
  onRead: () => void;
  onDismiss: () => void;
}) {
  const style = SEVERITY_STYLE[notification.severity];
  const isUnread = notification.read_at === null;

  return (
    <div
      className={`group flex gap-2.5 border-b border-slate-100 px-3.5 py-2.5 last:border-0 dark:border-slate-800 ${
        isUnread ? 'bg-indigo-50/40 dark:bg-indigo-950/20' : ''
      }`}
    >
      <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${style.dot}`} />

      <div className="min-w-0 flex-1">
        <p className={`text-sm ${isUnread ? 'font-medium' : ''}`}>{notification.title}</p>
        {notification.body && (
          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{notification.body}</p>
        )}

        <div className="mt-1 flex items-center gap-2 text-xs">
          <span className="text-slate-400">{relativeTime(notification.created_at)}</span>
          {notification.amount && (
            <span className="font-medium text-slate-600 dark:text-slate-300">
              {formatMoney(notification.amount)}
            </span>
          )}
        </div>
      </div>

      <div className="flex shrink-0 flex-col gap-1 opacity-0 transition group-hover:opacity-100">
        {isUnread && (
          <button onClick={onRead} className="text-xs text-slate-400 hover:text-indigo-600" title="Marcar como lido">
            ✓
          </button>
        )}
        <button onClick={onDismiss} className="text-xs text-slate-400 hover:text-rose-600" title="Dispensar">
          ✕
        </button>
      </div>
    </div>
  );
}

/** "há 2 horas", "ontem" — mais legível que a data absoluta numa lista curta. */
function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60_000);

  if (minutes < 1) return 'agora';
  if (minutes < 60) return `há ${minutes} min`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `há ${hours}h`;

  const days = Math.floor(hours / 24);
  if (days === 1) return 'ontem';
  if (days < 30) return `há ${days} dias`;

  return new Date(iso).toLocaleDateString('pt-BR');
}
